import { isSixesCellWon, sixesCardLineIndices, type SixesCellOutcome } from "./sixesLiveData";
import {
    applySixesSwaps,
    buildSixesMovementPlan,
    movementPlanSwaps,
    type SixesBucketMap,
    type SixesLineId,
} from "./sixesMovement";

/** Movement-plan line id → card-line key — mirrors backend `SIXES_LINE_ID_TO_CARD_KEY`. */
const SIXES_LINE_ID_TO_CARD_KEY: Partial<Record<SixesLineId, string>> = {
    COL2: "col:2",
    COL3: "col:3",
    COL0_FULL: "col:0",
    COL5_FULL: "col:5",
    ROW2: "row:2",
    ROW3: "row:3",
    DIAG_TLBR: "diag:0",
    DIAG_TRBL: "diag:1",
};

const LINE_KEYS_BY_DEFINITION = [
    ...Array.from({ length: 6 }, (_, row) => `row:${row}`),
    ...Array.from({ length: 6 }, (_, col) => `col:${col}`),
    "diag:0",
    "diag:1",
] as const;

function completedLineKeys(outcomes: readonly SixesCellOutcome[]): Set<string> {
    const keys = new Set<string>();
    const lines = sixesCardLineIndices();
    for (let i = 0; i < lines.length; i += 1) {
        const line = lines[i]!;
        const key = LINE_KEYS_BY_DEFINITION[i];
        if (
            key &&
            line.length >= 2 &&
            line.every((idx) => isSixesCellWon(outcomes[idx] ?? "pending"))
        ) {
            keys.add(key);
        }
    }
    return keys;
}

/**
 * Slide and natural line win counts — mirrors backend `countSixesPrizeCategories`.
 */
export function countSixesPrizeCategories(
    buckets: SixesBucketMap,
    outcomes: readonly SixesCellOutcome[],
): { slidesCount: number; naturalCount: number } {
    const plan = buildSixesMovementPlan(buckets, outcomes);
    const slidLineKeys = new Set<string>();
    let slidesCount = 0;
    for (const claim of plan.claims) {
        slidesCount += 1;
        const key = SIXES_LINE_ID_TO_CARD_KEY[claim.lineId];
        if (key) slidLineKeys.add(key);
    }

    const wonInPlaceKeys = completedLineKeys(outcomes);
    const postSlideOutcomes = applySixesSwaps(outcomes, movementPlanSwaps(plan));
    const naturalKeys = new Set<string>();
    for (const key of wonInPlaceKeys) {
        if (!slidLineKeys.has(key)) naturalKeys.add(key);
    }
    for (const key of completedLineKeys(postSlideOutcomes)) {
        if (!slidLineKeys.has(key)) naturalKeys.add(key);
    }

    return { slidesCount, naturalCount: naturalKeys.size };
}
